import React, { useState, useEffect } from 'react';
import {
  Accordion, Table, Badge, Alert, Form, Row, Col, Button, InputGroup, Modal
} from 'react-bootstrap';
import { FaCheck, FaEdit, FaTrash } from 'react-icons/fa';
import axios from 'axios';

const UaeRetentionTab = ({ userData, onScoreUpdate, showNotification, region, refreshActivities ,logToAccessLogs}) => {
  const [retentionPolicies, setRetentionPolicies] = useState([]);
  const [upcomingDeletions, setUpcomingDeletions] = useState([]);
  const [newPolicy, setNewPolicy] = useState({
    category: '',
    periodValue: '',
    periodUnit: 'Years',
    legalBasis: '',
    autoDelete: false
  });

  // New state for editing functionality
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [editPolicy, setEditPolicy] = useState({
    id: '',
    category: '',
    periodValue: '',
    periodUnit: 'Years',
    legalBasis: '',
    autoDelete: false
  });

  const headers = {
    Region: region,
    UserId: userData?.['cognito:username'] 
  };

  const logActivity = async (message, badge = "info") => {
    try {
      const payload = {
        action: badge,
        module: "UAE Data Retention",
        details: message
      };
      
      const response = await axios.post(
        "https://6x7bayihnj.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/activity-log/save",
        payload,     
        { headers }
      );
      
      if (refreshActivities) {
        await refreshActivities();
      }
      
    } catch (err) {
      console.error('❌ Error logging UAE data retention activity:', err);
      
      if (err.response) {
        console.error('🔥 Server Error Details:');
        console.error('Status:', err.response.status);
        console.error('Data:', err.response.data);
      }
      
      console.warn("Failed to log UAE retention activity:", err.message);
    }
  };

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const policyRes = await axios.get('https://2ocd8tkrkl.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/retention-policy/list', { headers });
      setRetentionPolicies(policyRes.data);

      const deletionRes = await axios.get('https://phapgcg5w2.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/retention-policy/scheduled-deletions', { headers });
      setUpcomingDeletions(deletionRes.data);

      // Calculate UAE PDPL compliance score
      const MIN_CATEGORIES = [
        'Customer Personal Data',
        'Employee Records',
        'Financial Records',
        'Marketing Data',
        'Transaction Records',
        'Website Analytics',
        'Healthcare Data',
        'Government Relations'
      ];
      
      // UAE-specific retention limits
      const MAX_RETENTION_YEARS = 7; // UAE corporate tax requires 7 years max
      const MIN_FINANCIAL_RETENTION = 5; // UAE financial sector minimum 5 years

      const totalPolicies = policyRes.data.length;
      const autoDeleteCount = policyRes.data.filter(p => p.autoDelete).length;
      const fullyConfigured = policyRes.data.filter(
        p => p.category && p.legalBasis && p.periodValue > 0
      ).length;
      
      // UAE-specific compliance checks
      const overdueRetention = policyRes.data.filter(
        p => p.periodUnit === 'Years' && p.periodValue > MAX_RETENTION_YEARS
      ).length;
      
      const financialDataUnderRetained = policyRes.data.filter(
        p => (p.category.toLowerCase().includes('financial') || 
              p.category.toLowerCase().includes('transaction')) &&
             p.periodUnit === 'Years' && p.periodValue < MIN_FINANCIAL_RETENTION
      ).length;
      
      const missingCategories = MIN_CATEGORIES.filter(
        cat => !policyRes.data.some(p => p.category === cat)
      ).length;

      let score = 0;
      if (totalPolicies === 0) {
        score = 0;
      } else {
        score = 100;
        score -= missingCategories * 12; // -12 per missing critical category
        score -= overdueRetention * 25; // -25 per overdue retention (UAE compliance)
        score -= financialDataUnderRetained * 20; // -20 per under-retained financial data
        score -= (totalPolicies - fullyConfigured) * 10; // -10 per incomplete policy
        
        // Bonus for auto-delete implementation (UAE PDPL encourages automation)
        const autoDeleteBonus = Math.min(10, (autoDeleteCount / totalPolicies) * 10);
        score += autoDeleteBonus;
        
        score = Math.max(0, Math.min(100, score));
      }

      onScoreUpdate(Math.round(score));

      if (policyRes.data.length > 0) {
        await logActivity(`Reviewed ${policyRes.data.length} UAE retention policies - Compliance score: ${Math.round(score)}%`, "info");
      }

    } catch (error) {
      console.error('Error fetching UAE policies or deletions', error);
    }
  };

  const handleNewPolicyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewPolicy((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // New function to handle editing policy changes
  const handleEditPolicyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditPolicy((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Function to open edit modal
  const openEditModal = (policy) => {
    setEditingPolicy(policy);
    setEditPolicy({
      id: policy.id,
      category: policy.category,
      periodValue: policy.periodValue,
      periodUnit: policy.periodUnit,
      legalBasis: policy.legalBasis,
      autoDelete: policy.autoDelete || false
    });
    setShowEditModal(true);
  };

  // Function to close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingPolicy(null);
    setEditPolicy({
      id: '',
      category: '',
      periodValue: '',
      periodUnit: 'Years',
      legalBasis: '',
      autoDelete: false
    });
  };

  // Function to handle policy update
  const handleUpdatePolicy = async (e) => {
    e.preventDefault();
    const { id, category, periodValue, periodUnit, legalBasis, autoDelete } = editPolicy;
    const numVal = Number(periodValue);

    if (!category || !numVal || numVal <= 0 || !legalBasis) {
      await logActivity(`Attempted to update UAE retention policy with incomplete data - Category: ${category || 'missing'}, Period: ${numVal || 'missing'}, Legal basis: ${legalBasis || 'missing'}`, "warning");
      showNotification("Please fill all required fields", "danger");
      return;
    }

    // UAE-specific validation
    if ((category.toLowerCase().includes('financial') || category.toLowerCase().includes('transaction')) 
        && periodUnit === 'Years' && numVal < 5) {
      await logActivity(`Attempted to update UAE financial retention policy below minimum 5-year requirement`, "warning");
      showNotification("Financial and transaction data must be retained for minimum 5 years under UAE regulations", "danger");
      return;
    }

    const payload = {
      category: category.trim(),
      periodValue: numVal,
      periodUnit,
      legalBasis,
      autoDelete
    };

    try {
      const response = await axios.put(
        `https://b4he6j22jc.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/retention-policy/update/${id}`,
        payload,
        { headers }
      );
      
      if (response.data.includes('Error')) {
        showNotification(response.data, 'danger');
        return;
      }

      await logActivity(`Updated UAE data retention policy: ${payload.category} - ${payload.periodValue} ${payload.periodUnit} (Legal basis: ${payload.legalBasis}${payload.autoDelete ? ', Auto-delete enabled' : ''})`, "success");
 logToAccessLogs?.({
        action:      'Retention Policy Updated',
        severity:    'success',
        description: `Updated UAE retention policy for ${payload.category} — ${payload.periodValue} ${payload.periodUnit}`,});
      await fetchPolicies();
      showNotification('UAE retention policy updated successfully!');
      closeEditModal();
      
    } catch (error) {
      console.error('Error updating UAE policy:', error);
      showNotification('Error updating UAE retention policy', 'danger');
    }
  };

  // Function to handle policy deletion
  const handleDeletePolicy = async (policyId, policyCategory) => {
    if (window.confirm(`Are you sure you want to delete the retention policy for "${policyCategory}"?`)) {
      try {
        await axios.delete(
          `https://61qlfrxg2m.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/retention-policy/delete/${policyId}`,
          { headers }
        );
        
        await logActivity(`Deleted UAE data retention policy: ${policyCategory}`, "warning");
         logToAccessLogs?.({
        action:      'Retention Policy Deleted',
        severity:    'success',
        description: `Deleted UAE retention policy for ${policyId}`,});
        await fetchPolicies();
        showNotification('UAE retention policy deleted successfully!');
        
      } catch (error) {
        console.error('Error deleting UAE policy:', error);
        showNotification('Error deleting UAE retention policy', 'danger');
      }
    }
  };

  const handlePolicySubmit = async (e) => {
    e.preventDefault();
    const { category, periodValue, periodUnit, legalBasis, autoDelete } = newPolicy;
    const numVal = Number(periodValue);

    if (!category || !numVal || numVal <= 0 || !legalBasis) {
      await logActivity(`Attempted to create UAE retention policy with incomplete data - Category: ${category || 'missing'}, Period: ${numVal || 'missing'}, Legal basis: ${legalBasis || 'missing'}`, "warning");
      showNotification("Please fill all required fields", "danger");
      return;
    }

    // UAE-specific validation
    if ((category.toLowerCase().includes('financial') || category.toLowerCase().includes('transaction')) 
        && periodUnit === 'Years' && numVal < 5) {
      await logActivity(`Attempted to create UAE financial retention policy below minimum 5-year requirement`, "warning");
      showNotification("Financial and transaction data must be retained for minimum 5 years under UAE regulations", "danger");
      return;
    }

    const payload = {
      category: category.trim(),
      periodValue: numVal,
      periodUnit,
      legalBasis,
      autoDelete
    };

    try {
      await axios.post('https://obxyyapsyf.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/retention-policy/create', payload, { headers });
      await logActivity(`Created UAE data retention policy: ${payload.category} - ${payload.periodValue} ${payload.periodUnit} (Legal basis: ${payload.legalBasis}${payload.autoDelete ? ', Auto-delete enabled' : ''})`, "success");
       logToAccessLogs?.({
        action:      'Retention Policy Created',
        severity:    'success',
        description: `Created GDPR retention policy for ${payload.category} — ${payload.periodValue} ${payload.periodUnit}`,});
      await fetchPolicies();
      showNotification('UAE retention policy created successfully!');
      
      setNewPolicy({
        category: '',
        periodValue: '',
        periodUnit: 'Years',
        legalBasis: '',
        autoDelete: false
      });
    } catch (error) {
      console.error('Error saving UAE policy:', error);
      showNotification('Error saving UAE retention policy', 'danger');
    }
  };

  // UAE-specific legal basis options
  const UAE_LEGAL_BASIS_OPTIONS = [
    'UAE PDPL Art. 5 - Purpose limitation and storage limitation',
    'UAE Corporate Tax Law - 7-year retention requirement',
    'UAE VAT Law - 5-year retention requirement',
    'Central Bank UAE regulations - Financial data retention',
    'UAE Labor Law - Employee records retention',
    'UAE Commercial Law - Business records retention',
    'Contractual obligation under UAE law',
    'Legitimate business interest under UAE PDPL',
    'Legal obligation under UAE federal law',
    'Consent of data subject for extended retention',
    'Vital interests protection under UAE PDPL',
    'Public interest task under UAE law'
  ];

 const [activeAccordionKey, setActiveAccordionKey] = useState(null);
  

  return (
    <>
      <div className="border rounded">
  <div
    className="d-flex justify-content-between align-items-center p-3" 
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "0" ? null : "0")
    }
  >
    <h6>Active UAE Data Retention Policies</h6>
    <span>{activeAccordionKey === "0" ? "▲" : "▼"}</span>
  </div>

  {activeAccordionKey === "0" && (
    <div className="p-3 border-top">
            <Alert variant="info" className="mb-3">
              <strong>UAE Personal Data Protection Law Compliance:</strong> Personal data must not be kept after 
              fulfilling the purpose of processing (Article 5). Financial institutions must retain personal data 
              for minimum 5 years, while corporate tax records require 7-year retention.
            </Alert>

            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Data Category</th>
                  <th>Retention Period</th>
                  <th>Legal Basis</th>
                  <th>Auto-Delete</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {retentionPolicies.map((policy, index) => (
                  <tr key={index}>
                    <td>{policy.category}</td>
                    <td>
                      {policy.periodValue} {policy.periodUnit}
                      {/* UAE compliance indicators */}
                      {policy.category.toLowerCase().includes('financial') && 
                       policy.periodUnit === 'Years' && policy.periodValue < 5 && (
                        <Badge bg="warning" className="ms-2">Below UAE Minimum</Badge>
                      )}
                      {policy.periodUnit === 'Years' && policy.periodValue > 7 && (
                        <Badge bg="danger" className="ms-2">Exceeds UAE Recommended</Badge>
                      )}
                    </td>
                    <td>{policy.legalBasis}</td>
                    <td>{policy.autoDelete ? <FaCheck className="text-success" /> : '—'}</td>
                    <td>
                      <Badge bg="success">{policy.status || 'Active'}</Badge>
                    </td>
                    <td>
                      <Button 
                        size="sm" 
                        variant="outline-primary" 
                        className="me-1"
                        onClick={() => openEditModal(policy)}
                        title="Edit Policy"
                      >
                        <FaEdit />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline-danger"
                        onClick={() => handleDeletePolicy(policy.id, policy.category)}
                        title="Delete Policy"
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
         </div>
  )}

        <div
    className="d-flex justify-content-between align-items-center p-3" 
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "1" ? null : "1")
    }
  >
    <h6>Scheduled Data Deletions</h6>
    <span>{activeAccordionKey === "1" ? "▲" : "▼"}</span>
  </div>

  {activeAccordionKey === "1" && (
    <div className="p-3 border-top">
            <Alert variant="warning" className="mb-3">
              <strong>UAE PDPL Requirement:</strong> Personal data must be deleted or anonymized once the 
              retention purpose is fulfilled. Automated deletion helps ensure compliance with Article 5.
            </Alert>

            <Alert variant="info">
              <strong>Upcoming Deletions:</strong>
              {upcomingDeletions.length === 0 ? ( 
                <div>No scheduled deletions currently configured.</div>
              ) : (
                upcomingDeletions.map((item, index) => (
                  <div key={index}>
                    {item.category} → Deletion scheduled for {item.nextDeletionDate}
                  </div>
                ))
              )}
            </Alert>
          </div>
  )}

       <div
    className="d-flex justify-content-between align-items-center p-3" 
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "2" ? null : "2")
    }
  >
    <h6>Create New UAE Retention Policy</h6>
    <span>{activeAccordionKey === "2" ? "▲" : "▼"}</span>
  </div>

  {activeAccordionKey === "2" && (
    <div className="p-3 border-top">
            <Alert variant="warning" className="mb-3">
              <strong>UAE Compliance Guidelines:</strong>
              <ul className="mb-0">
                <li>Financial and transaction data: Minimum 5 years (Central Bank UAE)</li>
                <li>Corporate tax records: 7 years (UAE Corporate Tax Law)</li>
                <li>VAT records: 5 years (UAE VAT Law)</li>
                <li>Employee records: 7 years recommended (UAE Labor Law)</li>
                <li>General personal data: Only as long as necessary for purpose (UAE PDPL Article 5)</li>
              </ul>
            </Alert>

            <Form onSubmit={handlePolicySubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Data Category</Form.Label>
                    <Form.Select
                      name="category"
                      value={newPolicy.category}
                      onChange={handleNewPolicyChange}
                      required
                    >
                      <option value="">Select Data Category</option>
                      <option value="Customer Personal Data">Customer Personal Data</option>
                      <option value="Employee Records">Employee Records</option>
                      <option value="Financial Records">Financial Records</option>
                      <option value="Transaction Records">Transaction Records</option>
                      <option value="Marketing Data">Marketing Data</option>
                      <option value="Website Analytics">Website Analytics</option>
                      <option value="Healthcare Data">Healthcare Data</option>
                      <option value="Government Relations">Government Relations</option>
                      <option value="VAT Records">VAT Records</option>
                      <option value="Corporate Tax Records">Corporate Tax Records</option>
                      <option value="Audit Records">Audit Records</option>
                      <option value="Legal Documents">Legal Documents</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Retention Period</Form.Label>
                    <Form.Control
                      type="number"
                      name="periodValue"
                      value={newPolicy.periodValue}
                      onChange={handleNewPolicyChange}
                      min="1"
                      max="10"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>&nbsp;</Form.Label>
                    <Form.Select
                      name="periodUnit"
                      value={newPolicy.periodUnit}
                      onChange={handleNewPolicyChange}
                    >
                      <option value="Days">Days</option>
                      <option value="Months">Months</option>
                      <option value="Years">Years</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Legal Basis for Retention</Form.Label>
                <Form.Select
                  name="legalBasis"
                  value={newPolicy.legalBasis}
                  onChange={handleNewPolicyChange}
                  required
                >
                  <option value="">Select Legal Basis</option>
                  {UAE_LEGAL_BASIS_OPTIONS.map((basis, index) => (
                    <option key={index} value={basis}>{basis}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="autoDelete"
                  checked={newPolicy.autoDelete}
                  onChange={handleNewPolicyChange}
                  label="Enable automatic deletion when retention period expires (Recommended for UAE PDPL compliance)"
                />
              </Form.Group>
              
              <Button type="submit" variant="primary">Create UAE Retention Policy</Button>
            </Form>
          </div>
  )}
      </div>

      {/* Edit Policy Modal */}
      <Modal show={showEditModal} onHide={closeEditModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit UAE Retention Policy</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <strong>UAE Compliance:</strong> Ensure retention periods comply with UAE regulations. 
            Financial data requires minimum 5 years, corporate tax records need 7 years.
          </Alert>
          
          <Form onSubmit={handleUpdatePolicy}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Data Category</Form.Label>
                  <Form.Select
                    name="category"
                    value={editPolicy.category}
                    onChange={handleEditPolicyChange}
                    required
                  >
                    <option value="">Select Data Category</option>
                    <option value="Customer Personal Data">Customer Personal Data</option>
                    <option value="Employee Records">Employee Records</option>
                    <option value="Financial Records">Financial Records</option>
                    <option value="Transaction Records">Transaction Records</option>
                    <option value="Marketing Data">Marketing Data</option>
                    <option value="Website Analytics">Website Analytics</option>
                    <option value="Healthcare Data">Healthcare Data</option>
                    <option value="Government Relations">Government Relations</option>
                    <option value="VAT Records">VAT Records</option>
                    <option value="Corporate Tax Records">Corporate Tax Records</option>
                    <option value="Audit Records">Audit Records</option>
                    <option value="Legal Documents">Legal Documents</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Retention Period</Form.Label>
                  <Form.Control
                    type="number"
                    name="periodValue"
                    value={editPolicy.periodValue}
                    onChange={handleEditPolicyChange}
                    min="1"
                    max="10"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Unit</Form.Label>
                  <Form.Select
                    name="periodUnit"
                    value={editPolicy.periodUnit}
                    onChange={handleEditPolicyChange}
                  >
                    <option value="Days">Days</option>
                    <option value="Months">Months</option>
                    <option value="Years">Years</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Legal Basis for Retention</Form.Label>
              <Form.Select
                name="legalBasis"
                value={editPolicy.legalBasis}
                onChange={handleEditPolicyChange}
                required
              >
                <option value="">Select Legal Basis</option>
                {UAE_LEGAL_BASIS_OPTIONS.map((basis, index) => (
                  <option key={index} value={basis}>{basis}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="autoDelete"
                checked={editPolicy.autoDelete}
                onChange={handleEditPolicyChange}
                label="Enable automatic deletion when retention period expires (Recommended for UAE PDPL compliance)"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEditModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdatePolicy}>
            Update Policy
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UaeRetentionTab;

